import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import { ArrowRight, Loader2 } from 'lucide-react';

/**
 * Spam-protected contact form with:
 * - Honeypot field (hidden field that bots fill)
 * - Time-based validation (forms submitted too fast are rejected)
 * - Basic input sanitization
 * - Rate limiting (prevents rapid submissions)
 */
const SpamProtectedForm = ({ 
  onSubmit, 
  emailSubject = 'Contact Form Inquiry',
  recipientEmail = 'sales@voxitconsulting.media',
  submitText = 'Send Message',
  className = '',
  fields = ['name', 'email', 'company', 'message']
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    honeypot: '', // Hidden field - bots will fill this
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSubmitTime, setLastSubmitTime] = useState(0);
  const formLoadTime = useRef(Date.now());

  const sanitizeInput = (input) => {
    // Remove potentially harmful characters and scripts
    return input
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<[^>]*>/g, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+=/gi, '')
      .trim();
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Spam check 1: Honeypot field should be empty
    if (formData.honeypot) {
      console.log('Bot detected via honeypot');
      toast.success('Message sent successfully!'); // Fake success to fool bots
      return;
    }

    // Spam check 2: Form submitted too fast (less than 3 seconds)
    const timeSinceLoad = Date.now() - formLoadTime.current;
    if (timeSinceLoad < 3000) {
      console.log('Form submitted too fast');
      toast.error('Please take your time filling out the form.');
      return;
    }

    // Spam check 3: Rate limiting (prevent submissions within 30 seconds)
    const timeSinceLastSubmit = Date.now() - lastSubmitTime;
    if (lastSubmitTime > 0 && timeSinceLastSubmit < 30000) {
      toast.error('Please wait before submitting again.');
      return;
    }

    // Validate required fields
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error('Please fill in all required fields.');
      return;
    }

    // Validate email format
    if (!validateEmail(formData.email)) {
      toast.error('Please enter a valid email address.');
      return;
    }

    // Validate minimum message length
    if (fields.includes('message') && formData.message.trim().length < 10) {
      toast.error('Please provide more details in your message.');
      return;
    }

    // Check for spam patterns in message
    const spamPatterns = [
      /\b(viagra|casino|lottery|winner|click here|buy now|limited time)\b/gi,
      /http[s]?:\/\/[^\s]+/gi, // URLs in message
      /\[url=/gi,
      /\{link\}/gi,
    ];

    for (const pattern of spamPatterns) {
      if (pattern.test(formData.message)) {
        toast.error('Your message contains content that cannot be sent.');
        return;
      }
    }

    setIsSubmitting(true);
    setLastSubmitTime(Date.now());

    try {
      // Sanitize all inputs
      const sanitizedData = {
        name: sanitizeInput(formData.name),
        email: sanitizeInput(formData.email),
        company: sanitizeInput(formData.company),
        message: sanitizeInput(formData.message),
      };

      // Build email body
      const subject = encodeURIComponent(`${emailSubject} from ${sanitizedData.name}`);
      const body = encodeURIComponent(
        `Name: ${sanitizedData.name}\n` +
        `Email: ${sanitizedData.email}\n` +
        `Company: ${sanitizedData.company || 'Not provided'}\n\n` +
        `Message:\n${sanitizedData.message}`
      );

      // Open email client
      window.location.href = `mailto:${recipientEmail}?subject=${subject}&body=${body}`;
      
      toast.success('Opening email client...');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        company: '',
        message: '',
        honeypot: '',
      });
      
      // Reset form load time for next submission
      formLoadTime.current = Date.now();

    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={`contact-form ${className}`}>
      {/* Honeypot field - hidden from users, bots will fill it */}
      <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0 }} aria-hidden="true">
        <label htmlFor="website">Website</label>
        <input
          type="text"
          id="website"
          name="honeypot"
          value={formData.honeypot}
          onChange={handleChange}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      {fields.includes('name') && (
        <div className="form-group">
          <label htmlFor="name">Full Name <span className="text-red-500">*</span></label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="Your name"
            maxLength={100}
            autoComplete="name"
          />
        </div>
      )}

      {fields.includes('email') && (
        <div className="form-group">
          <label htmlFor="email">Email <span className="text-red-500">*</span></label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="your@email.com"
            maxLength={150}
            autoComplete="email"
          />
        </div>
      )}

      {fields.includes('company') && (
        <div className="form-group">
          <label htmlFor="company">Company</label>
          <input
            type="text"
            id="company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            placeholder="Company name (optional)"
            maxLength={100}
            autoComplete="organization"
          />
        </div>
      )}

      {fields.includes('message') && (
        <div className="form-group">
          <label htmlFor="message">Message <span className="text-red-500">*</span></label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows={4}
            placeholder="Tell us about your project..."
            maxLength={2000}
          />
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="btn-service-primary w-full"
      >
        {isSubmitting ? (
          <>
            <Loader2 size={18} className="animate-spin" />
            Sending...
          </>
        ) : (
          <>
            {submitText} <ArrowRight size={18} />
          </>
        )}
      </button>
    </form>
  );
};

export default SpamProtectedForm;
